import { getCurrentProject, getProjectIdFromUrl } from "./projectUtils";

/**
 * URL Project Synchronization utility
 * Ensures Redux store stays in sync with URL project parameter
 */

let currentProjectId = null;
let store = null;
let listeners = [];

/**
 * Initialize the URL project synchronization
 * @param {Object} reduxStore - The Redux store instance
 */
export const initializeUrlProjectSync = (reduxStore) => {
  store = reduxStore;

  // Initial sync
  syncProjectFromUrl();

  // Listen for URL changes (back/forward buttons, manual URL changes)
  window.addEventListener("popstate", syncProjectFromUrl);

  // Listen for focus events (switching between tabs)
  window.addEventListener("focus", syncProjectFromUrl);

  // Check for URL changes periodically (for cases where URL changes aren't caught)
  setInterval(syncProjectFromUrl, 1000);
};

/**
 * Sync project from URL parameter to Redux store
 */
const syncProjectFromUrl = () => {
  const urlProjectId = getProjectIdFromUrl();

  // Only update if project ID has changed
  if (urlProjectId !== currentProjectId) {
    currentProjectId = urlProjectId;

    if (urlProjectId) {
      const project = getCurrentProject();

      if (project && store) {
        // Get current state to check if project is different
        const currentState = store.getState();
        const currentStoreProject = currentState.indexReducer?.project;

        // Only dispatch if project is actually different
        if (!currentStoreProject || currentStoreProject._id !== project._id) {
          console.log("Syncing project from URL:", project._id);

          // Dispatch project selection to update Redux store
          store.dispatch({
            type: "PROJECT_SELECTED",
            payload: { project },
          });
        }

        // Always notify listeners (components might need to re-render)
        listeners.forEach((listener) => listener(project));
      }
    } else {
      // No project ID in URL, clear current project
      currentProjectId = null;
      listeners.forEach((listener) => listener(null));
    }
  }
};

/**
 * Add a listener for project changes
 * @param {Function} callback - Called when project changes
 * @returns {Function} Unsubscribe function
 */
export const onProjectChange = (callback) => {
  listeners.push(callback);

  // Return unsubscribe function
  return () => {
    const index = listeners.indexOf(callback);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
};

/**
 * Force sync project from URL (useful for manual triggering)
 */
export const forceSyncProject = () => {
  currentProjectId = null; // Reset to force update
  syncProjectFromUrl();
};

/**
 * Get current project ID being tracked
 */
export const getCurrentTrackedProjectId = () => {
  return currentProjectId;
};

/**
 * Cleanup function
 */
export const cleanupUrlProjectSync = () => {
  window.removeEventListener("popstate", syncProjectFromUrl);
  window.removeEventListener("focus", syncProjectFromUrl);
  listeners = [];
  currentProjectId = null;
  store = null;
};
